import { useState, useMemo, useCallback } from "react";
import Data from "../../../apiData/yoga2.json";

// ─── Build a flat index of every asana across all categories ──────────────────
// Done once at module level — never recalculated on re-render.
// Deduplicated by videoPath: if the same video exists in multiple categories
// (e.g. Pavanamuktasana in Wellness AND Diseases), only the first occurrence
// is kept so it never appears more than once in search results.
const _rawIndex = Data.flatMap((cat) =>
  (cat.subcat ?? []).flatMap((sub) =>
    (sub.subSubCat ?? []).map((asana) => ({
      videoPath:   asana.asnVideoPath,
      sN:          asana.sN,
      name:        asana.subCatName,
      duration:    asana.asnVideoDuration,
      description: asana.description ?? "",
      tags:        (asana.asnTags ?? "")
                     .split(",")
                     .map((t) => t.trim())
                     .filter(Boolean),
      rawTags:     asana.asnTags ?? "",
      category:    cat.category,
      subCatName:  sub.subCatName,
    }))
  )
);

// Map preserves insertion order → first occurrence per videoPath wins.
const ASANA_INDEX = [
  ...new Map(_rawIndex.map((a) => [a.videoPath, a])).values(),
];

// All unique, non-empty tags sorted alphabetically (deduplicated + casing fixed)
export const ALL_TAGS = [
  ...new Map(
    ASANA_INDEX.flatMap((a) => a.tags).map((t) => [t.toLowerCase(), t])
  ).values(),
].sort();

/**
 * useAsanaSearch
 *
 * Manages disease/tag-based asana search state and returns everything the UI
 * needs to render the search bar, tag chips, and results.
 *
 * @returns {{
 *   query:          string,
 *   activeTag:      string | null,
 *   results:        Array,
 *   isSearching:    boolean,
 *   setQuery:       function,
 *   setActiveTag:   function,
 *   clearSearch:    function,
 * }}
 */
const useAsanaSearch = () => {
  const [query,     setQueryRaw]  = useState("");
  const [activeTag, setActiveTag] = useState(null);

  // Trimmed, lowercased query for matching
  const normQuery = useMemo(() => query.trim().toLowerCase(), [query]);

  const isSearching = normQuery.length > 0 || activeTag !== null;

  const results = useMemo(() => {
    if (!isSearching) return [];

    return ASANA_INDEX.filter((asana) => {
      const tagsLower = asana.tags.map((t) => t.toLowerCase());

      // Tag chip selected — must include that tag exactly
      const tagMatch = activeTag
        ? tagsLower.includes(activeTag.toLowerCase())
        : true;

      // Text query — match against name, tags, category, subCatName
      const textMatch = normQuery
        ? asana.name.toLowerCase().includes(normQuery) ||
          tagsLower.some((t) => t.includes(normQuery)) ||
          asana.category.toLowerCase().includes(normQuery) ||
          asana.subCatName.toLowerCase().includes(normQuery)
        : true;

      return tagMatch && textMatch;
    });
  }, [normQuery, activeTag, isSearching]);

  const setQuery = useCallback((val) => {
    setQueryRaw(val);
    // Clear tag chip when the user starts typing freely
    if (val.trim()) setActiveTag(null);
  }, []);

  const handleTagClick = useCallback((tag) => {
    // Toggle: clicking the active tag deselects it
    setActiveTag((prev) => (prev === tag ? null : tag));
    setQueryRaw("");
  }, []);

  const clearSearch = useCallback(() => {
    setQueryRaw("");
    setActiveTag(null);
  }, []);

  return {
    query,
    activeTag,
    results,
    isSearching,
    setQuery,
    setActiveTag: handleTagClick,
    clearSearch,
  };
};

export default useAsanaSearch;